import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Linkedin, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CouncilPage = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [publishedMembers, setPublishedMembers] = useState({
    founding_member: [],
    council_member: [],
    advisor: [],
    council_ambassador: [],
  });

  useEffect(() => {
    axios.get(`${API}/council/published-members`)
      .then((res) => setPublishedMembers(res.data || {}))
      .catch(() => {});
  }, []);

  const MemberCard = ({ member, testId }) => (
    <button
      onClick={() => setSelectedMember(member)}
      className="text-left bg-white border border-slate-200 hover:border-slate-400 transition-all group cursor-pointer"
      data-testid={testId}
    >
      <div className="w-full aspect-square overflow-hidden bg-slate-100">
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-slate-900">{member.name}</h3>
        <p className="text-xs text-cyan-700 font-medium mt-1">{member.role}</p>
        <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
          <MapPin className="w-3 h-3" />
          <span>{member.location}</span>
        </div>
      </div>
    </button>
  );

  return (
    <div data-testid="council-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              The SAFE Council
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              The SAFE Council defines the SAFE standard.
            </p>
            <p className="text-lg md:text-xl text-slate-300">
              It is an independent body responsible for determining what constitutes safety in AI systems operating in child-facing and high-risk environments.
            </p>
          </div>
        </div>
      </section>

      {/* Authority */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Authority</p>
            <p className="text-lg text-slate-900 font-semibold mb-8">
              The SAFE Council is not an advisory group.
            </p>
            <p className="text-lg text-slate-700 mb-6">It defines:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Safety thresholds</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Harm classifications</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Intervention requirements</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Updates to the standard</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              Its decisions determine what systems must meet to be considered safe.
            </p>
          </div>
        </div>
      </section>

      {/* Core Principle */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-xl md:text-2xl font-semibold tracking-tight">
            The entity that defines safety must be independent<br />
            of those who build systems<br />
            and those who verify them.
          </p>
        </div>
      </section>

      {/* Independence */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Independence</p>
            <p className="text-lg text-slate-700 mb-6">Council members do not:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Build systems seeking certification</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Verify systems</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Hold commercial influence over certification outcomes</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              Independence is required to preserve integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Structure */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Structure</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">
                The SAFE Council is composed of distinct, non-overlapping roles.
              </p>
              <p className="text-lg text-slate-700">
                Each role is responsible for a defined domain of the standard.
              </p>
            </div>
            <p className="text-lg text-slate-700 mb-6">Core domains include:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Behavioral Adversarial Integrity</p>
              </div>
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Systems & Infrastructure Integrity</p>
              </div>
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Research & Evidence</p>
              </div>
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Regional & Real-World Implementation</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold mb-2">Roles are function-specific.</p>
            <p className="text-lg text-slate-900 font-semibold">They are not interchangeable.</p>
          </div>
        </div>
      </section>

      {/* Role of Members */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Role of Members</p>
            <p className="text-lg text-slate-700 mb-6">Council members are responsible for:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Defining and refining the SAFE standard</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Contributing domain expertise to risk classification</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Ensuring real-world applicability across jurisdictions</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Maintaining the integrity of the standard over time</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold mb-2">Membership is not symbolic.</p>
            <p className="text-lg text-slate-900 font-semibold">It carries responsibility for outcomes.</p>
          </div>
        </div>
      </section>

      {/* Structural Importance */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Structural Importance</p>
            <div className="space-y-4">
              <p className="text-lg text-slate-300">Without independent definition, verification has no foundation.</p>
              <p className="text-lg text-white font-semibold">Without the Council, there is no standard to verify against.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Council Seats */}
      {(publishedMembers.council_member || []).length > 0 && (
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-8">Council Seats</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(publishedMembers.council_member || []).map((member, index) => (
              <MemberCard key={`${member.name}-${index}`} member={member} testId={`council-seat-${index}`} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Founders */}
      {(publishedMembers.founding_member || []).length > 0 && (
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-slate-500 mb-8">Founders</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(publishedMembers.founding_member || []).map((member, index) => (
              <MemberCard key={`${member.name}-${index}`} member={member} testId={`founder-${index}`} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Advisors */}
      {(publishedMembers.advisor || []).length > 0 && (
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-slate-500 mb-8">Advisors</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(publishedMembers.advisor || []).map((member, index) => (
              <MemberCard key={`${member.name}-${index}`} member={member} testId={`advisor-${index}`} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Council Ambassadors */}
      {(publishedMembers.council_ambassador || []).length > 0 && (
        <section className="section-padding bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto container-padding">
            <p className="caption text-cyan-700 mb-8">Council Ambassadors</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(publishedMembers.council_ambassador || []).map((member, index) => (
                <MemberCard key={`${member.name}-${index}`} member={member} testId={`ambassador-${index}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Join the Council */}
      <section className="section-padding bg-slate-900 text-white border-t border-slate-800" data-testid="join-council-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="caption text-cyan-400 mb-4">Join the Council</p>
              <h2 className="text-base md:text-lg font-semibold text-white mb-6">
                Applications are reviewed individually.
              </h2>
              <div className="space-y-4 text-slate-300 text-base">
                <p>
                  The Council is composed of independent contributors who help define, interpret,
                  and steward the standard. Membership is not open enrolment. Every application is
                  reviewed against independence, expertise, and contribution.
                </p>
                <p>
                  If you believe your background is relevant, complete the Council application.
                  You will be contacted following review.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 lg:pt-14">
              <div className="border border-slate-700 bg-slate-800/60 p-8">
                <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                  The application captures your details, public profile, expertise areas, and
                  reasons for joining. Submission does not constitute appointment.
                </p>
                <Link
                  to="/onboarding"
                  className="inline-flex items-center justify-center w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-6 py-3.5 transition-colors duration-200"
                  data-testid="btn-apply-council"
                >
                  Apply to the Council
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Line + CTA */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900 mb-12">
            If safety is defined by those who build or verify systems,<br />
            it is not independent.
          </p>
          <Link
            to="/governance"
            className="btn-primary"
            data-testid="btn-view-governance"
          >
            View Governance Structure
          </Link>
        </div>
      </section>

      {/* Member Modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMember(null)}
          data-testid="member-modal-overlay"
        >
          <div
            className="bg-white w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="member-modal"
          >
            <div className="relative p-6">
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 w-8 h-8 bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                data-testid="close-member-modal"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
              <h2 className="text-lg font-bold text-slate-900 mb-1">{selectedMember.name}</h2>
              <p className="text-sm text-cyan-700 font-medium mb-2">{selectedMember.role}</p>
              <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span>{selectedMember.location}</span>
                </div>
                {selectedMember.linkedin && (
                  <a
                    href={selectedMember.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-cyan-700 hover:text-cyan-900 transition-colors"
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                    <span className="text-xs">LinkedIn</span>
                  </a>
                )}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{selectedMember.bio}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouncilPage;
